/**
 * First-party, cookieless web analytics (docs/SPEC.md §15, decision 42): what a page view and a first touch may
 * contain, and how they are classified. Pure; shared by the app (collection) and the API (validation, reports).
 * Nothing here identifies a person: paths are reduced to templates (no handles, ids or link tokens), referrers to
 * their domain, and the visitor key is a hash that changes every day (computed on the server).
 */

/** The app's own pages (apps/app/src/App.tsx). Anything else is recorded as '/other'. */
const APP_PAGES = new Set(['/', '/feed', '/collections', '/welcome', '/explore', '/library', '/compare', '/settings', '/billing', '/earn', '/ai', '/signin', '/connect', '/remove']);

/**
 * App paths reduced to templates from an allow-list, so no creator handle, collection id or share token is ever
 * recorded (a typed `/somecreator` becomes '/other').
 */
export function pathTemplate(path: string): string {
  const p = (path.split(/[?#]/)[0] ?? '/').replace(/\/+$/, '') || '/';
  if (/^\/c\/[^/]+(\/.*)?$/.test(p)) return '/c/:handle';
  if (/^\/collections\/[^/]+(\/.*)?$/.test(p)) return '/collections/:id';
  if (/^\/r\/[^/]+$/.test(p)) return '/r/:token';
  if (/^\/l\/[^/]+$/.test(p)) return '/l/:token';
  return APP_PAGES.has(p) ? p : '/other';
}

/** Referrer → bare domain (no path or query); '' for none, our own hosts, or anything unparsable. */
export function referrerDomain(referrer: string | null | undefined, ownHosts: string[] = []): string {
  if (!referrer) return '';
  try {
    const u = new URL(referrer);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return '';
    const host = u.hostname.toLowerCase().replace(/^www\./, '');
    if (ownHosts.some((h) => host === h || host.endsWith(`.${h}`))) return '';
    return host.slice(0, 100);
  } catch {
    return '';
  }
}

/**
 * UTM values: short, printable, lower case. Values that look personal (an email address, or a long run of digits
 * such as a phone number or a subscriber id merged in by an email tool) are dropped.
 */
export function cleanUtm(v: unknown): string {
  if (typeof v !== 'string') return '';
  if (v.includes('@') || /\d{7,}/.test(v.replace(/[\s().-]/g, ''))) return '';
  return v.trim().toLowerCase().replace(/[^\p{L}\p{N}._:+-]/gu, '_').slice(0, 60);
}

const AI_DOMAINS = ['chatgpt.com', 'chat.openai.com', 'openai.com', 'claude.ai', 'perplexity.ai', 'gemini.google.com', 'copilot.microsoft.com'];
const SEARCH_DOMAINS = ['bing.com', 'duckduckgo.com', 'search.yahoo.com', 'yahoo.com', 'ecosia.org', 'baidu.com', 'yandex.ru', 'yandex.com', 'search.brave.com'];
const SOCIAL_DOMAINS = ['instagram.com', 'l.instagram.com', 'facebook.com', 'l.facebook.com', 'm.facebook.com', 't.co', 'twitter.com', 'x.com', 'linkedin.com', 'lnkd.in', 'tiktok.com', 'youtube.com', 'youtu.be', 'reddit.com', 'threads.net', 'pinterest.com'];
const domainIn = (host: string, list: string[]) => list.some((d) => host === d || host.endsWith(`.${d}`));

export const CHANNELS = ['Referral / affiliate', 'Shared report', 'Paid', 'Email', 'Social', 'Campaign', 'Direct', 'AI assistants', 'Organic search', 'Other websites'] as const;
export type Channel = (typeof CHANNELS)[number];

/** Marketing channel from UTM / referrer / invite code (same rules as OutlierOS `channelOf`). */
export function channelOf(t: { source?: string; medium?: string; referrer?: string; ref?: boolean; path?: string }): Channel {
  const src = (t.source ?? '').toLowerCase();
  const med = (t.medium ?? '').toLowerCase();
  const ref = (t.referrer ?? '').toLowerCase();
  if (t.ref || src.startsWith('ref:') || med === 'affiliate' || med === 'referral') return 'Referral / affiliate';
  if ((src === 'share' && med === 'report') || t.path === '/r/:token' || t.path === '/l/:token') return 'Shared report';
  if (/cpc|ppc|paid|ads?$/.test(med)) return 'Paid';
  if (med === 'email' || src.includes('newsletter')) return 'Email';
  if (src || med) return /(instagram|facebook|twitter|x\.com|linkedin|tiktok|youtube|reddit|threads)/.test(src) ? 'Social' : 'Campaign';
  if (!ref) return 'Direct';
  if (domainIn(ref, AI_DOMAINS)) return 'AI assistants';
  if (domainIn(ref, SEARCH_DOMAINS) || /(^|\.)google\.[a-z.]+$/.test(ref)) return 'Organic search';
  if (domainIn(ref, SOCIAL_DOMAINS)) return 'Social';
  return 'Other websites';
}

export function deviceOf(ua: string | null | undefined): 'mobile' | 'tablet' | 'desktop' {
  const s = ua ?? '';
  return /iPad|Tablet/i.test(s) ? 'tablet' : /Mobi|Android|iPhone/i.test(s) ? 'mobile' : 'desktop';
}

/** Where a new account came from (stored on the user at sign-up; shown in Admin → user and Analytics). */
export interface FirstTouch { referrer: string; source: string; medium: string; campaign: string; landing: string; ref: boolean; at: number }

/** Validates a first touch sent by the browser (it is untrusted). */
export function cleanFirstTouch(v: unknown, now: number): FirstTouch | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const at = Number(o.at);
  return {
    referrer: referrerDomain(typeof o.referrer === 'string' && o.referrer ? `https://${o.referrer.replace(/^https?:\/\//, '')}` : ''),
    source: cleanUtm(o.source), medium: cleanUtm(o.medium), campaign: cleanUtm(o.campaign),
    // Unknown landing stays empty (never invented).
    landing: typeof o.landing === 'string' && o.landing ? pathTemplate(o.landing) : '',
    ref: o.ref === true,
    at: Number.isFinite(at) && at > 0 ? Math.min(at, now) : now,
  };
}
